import {
  STUDENT_HISTORY_FIELD_LABELS,
  REGISTRATION_STATUS_LABELS,
  CONTRACT_STATUS_LABELS,
} from '../models/student-history.model';
import { getGradeLabel, type ClassType } from '../models/grade.model';

const SCHOOL_SNAPSHOT_PREFIX = 'startInfo.school.';

/** Maximal so viele Felder eines Objekts werden in der Kurzfassung gezeigt. */
const MAX_OBJECT_ENTRIES = 4;

/** Maximal so viele Array-Einträge werden namentlich aufgezählt. */
const MAX_LIST_ENTRIES = 3;

/**
 * Felder, die einen Kalendertag meinen – die Uhrzeit (immer 00:00) wäre im
 * Verlauf nur Rauschen (#1193).
 */
const DATE_ONLY_FIELDS = new Set([
  'birthDate',
  'healthInfo.tetanusVaccinationDate',
  'careChangeEffectiveDate',
  'contract.cancellationEffectiveDate',
  'yearInfo.startDate',
]);

/**
 * Formatiert einen Firestore-Timestamp (Instanz oder Plain Object) als
 * deutsches Datum, standardmäßig mit Uhrzeit.
 */
export function formatHistoryTimestamp(ts: unknown, withTime = true): string {
  if (!ts || typeof ts !== 'object') return '–';
  const obj = ts as Record<string, unknown>;
  let date: Date;
  if (typeof (obj as { toDate?: () => Date }).toDate === 'function') {
    date = (obj as { toDate: () => Date }).toDate();
  } else if (typeof obj['seconds'] === 'number') {
    date = new Date((obj['seconds'] as number) * 1000);
  } else if (typeof obj['_seconds'] === 'number') {
    date = new Date((obj['_seconds'] as number) * 1000);
  } else {
    return '–';
  }
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

/**
 * Lesbares Label für einen Feldpfad.
 *
 * Für Altdaten aus dem Schul-Snapshot (`startInfo.school.*`) gibt es keine
 * gepflegten Labels — statt des Roh-Feldpfads wird daraus „Schule – Module"
 * o.ä. gebildet. Neue Änderungen an diesen Feldern werden gar nicht mehr
 * getrackt (siehe `shouldSkipFieldPath` in den Functions).
 */
export function formatHistoryFieldLabel(field: string): string {
  const label = STUDENT_HISTORY_FIELD_LABELS[field];
  if (label) return label;

  if (field.startsWith(SCHOOL_SNAPSHOT_PREFIX)) {
    const rest = field.slice(SCHOOL_SNAPSHOT_PREFIX.length);
    return `Schule – ${humanizePath(rest)}`;
  }

  return field;
}

/**
 * Formatiert einen Historien-Wert für die Anzeige.
 *
 * Objekte und Arrays werden bewusst NICHT als JSON gedumpt — das war der
 * Grund für die unlesbare Historie bei Schul-Snapshot-Änderungen.
 */
export function formatHistoryValue(value: unknown, field?: string): string {
  if (value === null || value === undefined || value === '') return '–';

  // Anmeldungsstatus als lesbares Label statt Rohwert (customStartDatePending …)
  if (field === 'registration.status' && typeof value === 'string') {
    return REGISTRATION_STATUS_LABELS[value] ?? value;
  }
  // Vertragsstatus als lesbares Label (#1185)
  if (field === 'contract.status' && typeof value === 'string') {
    return CONTRACT_STATUS_LABELS[value] ?? value;
  }
  // Klassen-Felder als lesbares Label (Vorklasse / 1. Klasse / Paktklasse)
  if (
    (field === 'currentGrade' || field === 'startingGrade') &&
    typeof value === 'number'
  ) {
    return getGradeLabel(value);
  }
  if (field === 'classType') {
    return (value as ClassType) === 'paktklasse' ? 'Paktklasse' : 'Regulär';
  }
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';

  if (Array.isArray(value)) return formatArray(value);

  if (typeof value === 'object') {
    if (isTimestampLike(value)) {
      return formatHistoryTimestamp(value, !DATE_ONLY_FIELDS.has(field ?? ''));
    }
    return formatObject(value as Record<string, unknown>);
  }

  return String(value);
}

function isTimestampLike(value: object): boolean {
  const obj = value as Record<string, unknown>;
  return (
    typeof (obj as { toDate?: () => Date }).toDate === 'function' ||
    typeof obj['seconds'] === 'number' ||
    typeof obj['_seconds'] === 'number'
  );
}

function formatArray(values: unknown[]): string {
  if (values.length === 0) return '–';

  const labels = values.map((entry) => entryLabel(entry));
  const named = labels.filter((label): label is string => label !== null);

  if (named.length !== values.length) {
    return `${values.length} ${values.length === 1 ? 'Eintrag' : 'Einträge'}`;
  }

  if (named.length > MAX_LIST_ENTRIES) {
    const shown = named.slice(0, MAX_LIST_ENTRIES).join(', ');
    return `${shown} … (${named.length} Einträge)`;
  }

  return named.join(', ');
}

function entryLabel(entry: unknown): string | null {
  if (entry === null || entry === undefined) return null;
  if (typeof entry !== 'object') return String(entry);
  if (Array.isArray(entry)) return null;

  const obj = entry as Record<string, unknown>;
  for (const key of ['title', 'name', 'description', 'label', 'fileName']) {
    const candidate = obj[key];
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate;
    }
  }
  return null;
}

function formatObject(obj: Record<string, unknown>): string {
  const address = formatAddress(obj);
  if (address) return address;

  const named = entryLabel(obj);
  if (named) return named;

  const primitives = Object.entries(obj).filter(
    ([, entryValue]) =>
      entryValue !== null &&
      entryValue !== undefined &&
      typeof entryValue !== 'object'
  );

  if (primitives.length === 0) return 'Details geändert';

  const shown = primitives
    .slice(0, MAX_OBJECT_ENTRIES)
    .map(
      ([key, entryValue]) =>
        `${humanizePath(key)}: ${
          typeof entryValue === 'boolean'
            ? entryValue
              ? 'Ja'
              : 'Nein'
            : String(entryValue)
        }`
    )
    .join(', ');

  return primitives.length > MAX_OBJECT_ENTRIES ? `${shown} …` : shown;
}

function formatAddress(obj: Record<string, unknown>): string | null {
  const formatted = obj['formattedAddress'];
  if (typeof formatted === 'string' && formatted.trim() !== '') {
    return formatted;
  }

  const street = typeof obj['street'] === 'string' ? obj['street'] : '';
  const zipCode = typeof obj['zipCode'] === 'string' ? obj['zipCode'] : '';
  const city = typeof obj['city'] === 'string' ? obj['city'] : '';

  if (!street && !zipCode && !city) return null;

  return [street, [zipCode, city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
}

/** `contactFirstName` → `Contact First Name`-artig, aber schonend. */
function humanizePath(path: string): string {
  return path
    .split('.')
    .map((segment) =>
      segment
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/^./, (char) => char.toUpperCase())
    )
    .join(' – ');
}
